/**
 * Authentication — email link only.
 *
 * D5 (Agreed 26 Aug 2026): passwordless, by email link rather than phone OTP.
 * The signal survey decided it — five of the fourteen Coast-to-Coast
 * checkpoints are WiFi-only, and an SMS code cannot reach them. There is no
 * password fallback and there must never be one; a password field reintroduces
 * exactly what D5 removes.
 */

import {
  isSignInWithEmailLink,
  onAuthStateChanged,
  onIdTokenChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';

import { getServices } from './firebase.js';

/**
 * Where the link comes back to. Kept in localStorage because the link may be
 * opened in a different tab — or a different browser — from the one that asked
 * for it, and Firebase requires the address to complete the sign-in.
 */
const PENDING_EMAIL_KEY = 'cairn.pendingEmail';

export interface Session {
  readonly uid: string;
  readonly email: string | null;
  readonly displayName: string | null;
  /** Event ids from the token claim. D53: membership, never the role. */
  readonly events: readonly string[];
  readonly superAdmin: boolean;
}

function readPendingEmail(): string | null {
  try {
    return window.localStorage.getItem(PENDING_EMAIL_KEY);
  } catch {
    // Private browsing, or storage disabled. The completion step asks instead.
    return null;
  }
}

function writePendingEmail(email: string): void {
  try {
    window.localStorage.setItem(PENDING_EMAIL_KEY, email);
  } catch {
    /* Not fatal — the completion step falls back to asking. */
  }
}

function clearPendingEmail(): void {
  try {
    window.localStorage.removeItem(PENDING_EMAIL_KEY);
  } catch {
    /* ignore */
  }
}

/** Sends the sign-in link. The address is remembered for the return trip. */
export async function sendLink(email: string): Promise<void> {
  const { auth } = await getServices();
  const trimmed = email.trim();

  await sendSignInLinkToEmail(auth, trimmed, {
    // Back to whatever host we are on, so this works identically on localhost,
    // a preview channel and cairn.creedapps.uk. Every host used this way must
    // be listed in Firebase Auth → Settings → Authorized domains.
    url: `${window.location.origin}/`,
    handleCodeInApp: true,
  });

  writePendingEmail(trimmed);
}

/** True when the current URL is a sign-in link waiting to be completed. */
export async function isPendingLink(): Promise<boolean> {
  const { auth } = await getServices();
  return isSignInWithEmailLink(auth, window.location.href);
}

/** The address the link was requested for, if this browser still knows it. */
export function pendingEmail(): string | null {
  return readPendingEmail();
}

/**
 * Completes sign-in from the link in the current URL.
 *
 * The query string is stripped afterwards so a reload does not attempt to
 * reuse a one-time link and fail confusingly.
 */
export async function completeLink(email: string): Promise<void> {
  const { auth } = await getServices();
  await signInWithEmailLink(auth, email.trim(), window.location.href);
  clearPendingEmail();
  window.history.replaceState({}, '', window.location.pathname);
}

export async function signOut(): Promise<void> {
  const { auth } = await getServices();
  await fbSignOut(auth);
}

function toSession(user: User, claims: Record<string, unknown>): Session {
  const events = claims['events'];
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    events: Array.isArray(events) ? events.filter((e): e is string => typeof e === 'string') : [],
    superAdmin: claims['superAdmin'] === true,
  };
}

/**
 * Observes the signed-in user and their claims.
 *
 * Uses onIdTokenChanged rather than onAuthStateChanged so a token refresh —
 * which is when a newly granted membership claim actually arrives — updates
 * the session. onAuthStateChanged alone would not fire for that.
 */
export async function watchSession(
  onChange: (session: Session | null) => void,
): Promise<() => void> {
  const { auth } = await getServices();

  const unsubscribe = onIdTokenChanged(auth, (user) => {
    if (user === null) {
      onChange(null);
      return;
    }
    void user.getIdTokenResult().then((result) => {
      onChange(toSession(user, result.claims));
    });
  });

  return unsubscribe;
}

/**
 * Forces a token refresh so a claim set moments ago by the Cloud Function is
 * visible now rather than within the hour. Used straight after first sign-in,
 * which is exactly when the membership claim is newest.
 */
export async function refreshClaims(): Promise<void> {
  const { auth } = await getServices();
  await auth.currentUser?.getIdToken(true);
}

export { onAuthStateChanged };
